import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { formatDateTime } from "../lib/dateFormat";
import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type AppNotification,
} from "../lib/api";
import PushToggle from "../components/pwa/PushToggle";

const TYPE_LABELS: Record<string, string> = {
  communication: "Comunicado",
  result_reported: "Reporte de resultado",
  result_approved: "Reporte aprobado",
  result_rejected: "Reporte rechazado",
  rules_published: "Reglamento",
  schedule_changed: "Cambio de horario",
  group_changed: "Cambio de grupo",
  system: "Aviso",
};

export default function AdminNotificationsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [busyAll, setBusyAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.notifications.listForAdmin(token);
      setItems(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los avisos.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const markOne = useCallback(
    async (id: number) => {
      if (!token) return;
      setBusyId(id);
      try {
        await api.notifications.markAdminRead(token, id);
        await load();
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "No pudimos marcarlo como leído.");
        } else {
          setError("Error de red. Intenta otra vez.");
        }
      } finally {
        setBusyId(null);
      }
    },
    [token, load],
  );

  const markAll = useCallback(async () => {
    if (!token) return;
    setBusyAll(true);
    try {
      await api.notifications.markAdminAllRead(token);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos marcar todos como leídos.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyAll(false);
    }
  }, [token, load]);

  // Sort: unread first, then newest first.
  const sorted = [...items].sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bd - ad;
  });

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">Avisos operativos</h1>
            <p className="card__subtitle">
              Reportes pendientes, comunicados y eventos relevantes para tus
              torneos.
            </p>
          </div>
          <div className="form__actions">
            <Link className="btn btn--ghost" to="/admin">
              Volver al inicio
            </Link>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={markAll}
              disabled={busyAll || unreadCount === 0}
            >
              {busyAll ? "Marcando…" : "Marcar todos como leídos"}
            </button>
          </div>
        </div>
        <p className="notif-summary">
          <span>
            <strong>{unreadCount}</strong> sin leer
          </span>
          <span className="muted">·</span>
          <span>{items.length} total</span>
        </p>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </section>

      <PushToggle />

      <section className="card">
        {loading ? (
          <p className="muted">Cargando avisos…</p>
        ) : sorted.length === 0 ? (
          <p className="muted">
            Sin avisos pendientes. Cuando un jugador reporte un resultado o se
            publique un comunicado, lo verás aquí.
          </p>
        ) : (
          <ul className="notif-list">
            {sorted.map((notif) => {
              const typeLabel =
                TYPE_LABELS[notif.notification_type] ?? notif.notification_type;
              return (
                <li
                  key={notif.id}
                  className={
                    notif.read
                      ? "notif-card"
                      : "notif-card notif-card--unread"
                  }
                >
                  <div className="notif-card__head">
                    <div className="notif-card__title-row">
                      <span
                        className={
                          notif.read
                            ? "notif-badge notif-badge--read"
                            : "notif-badge notif-badge--unread"
                        }
                      >
                        {notif.read ? "Leído" : "No leído"}
                      </span>
                      <h2 className="notif-card__title">{notif.title}</h2>
                    </div>
                    <span className="kbd">{typeLabel}</span>
                  </div>
                  <p className="notif-card__body">{notif.body}</p>
                  <div className="notif-card__meta">
                    <span>{formatDateTime(notif.created_at)}</span>
                    {notif.tournament_id && (
                      <span className="muted">
                        Torneo #{notif.tournament_id}
                      </span>
                    )}
                  </div>
                  <div className="notif-card__actions">
                    {notif.action_url && (
                      <Link
                        className="btn btn--ghost btn--compact"
                        to={notif.action_url}
                      >
                        Ver detalle
                      </Link>
                    )}
                    {!notif.read && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--compact"
                        onClick={() => markOne(notif.id)}
                        disabled={busyId === notif.id}
                      >
                        {busyId === notif.id ? "Marcando…" : "Marcar leído"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
